import {MouseEventHandler, useCallback, useEffect, useRef, useState} from "react";
import {MiniMap, useNodes, useReactFlow, useViewport} from "react-flow-renderer";
import makeStyles from "@mui/styles/makeStyles";
import {connect} from "react-redux";
import {IGraphData, UpdateModeData, updateModeData} from "../../redux/slices/editor";
import {selectModeData} from "../../redux/selectors/editor";
import {CancelOutlined} from "@mui/icons-material";
import {IconButton} from "@mui/material";

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: "absolute",
        top: 0,
        right: 0,
        zIndex: 10
    },
    content: {
        position: "absolute",
        right: theme.spacing(4),
        bottom: theme.spacing(4)
    },
    miniMap: {
        position: "relative",
        bottom: 0,
        right: 0
    }
}));

interface IMiniMapDrag {
    graphData: IGraphData
    updateModeData: UpdateModeData
}
function MiniMapDrag({graphData, updateModeData}: IMiniMapDrag) {
    const classes = useStyles();
    const contentRef = useRef<HTMLDivElement>(null);
    const {zoom} = useViewport();
    const {setCenter} = useReactFlow();
    const [panning, setPanning] = useState(false);
    const [bounds, setBounds] = useState({width: 0, height: 0})
    const nodes = useNodes();
    const handleCloseMap:MouseEventHandler = (event) => {
        event.preventDefault();
        updateModeData({
            showMiniMap: ! graphData.showMiniMap
        });
    }
    const setViewportFromMiniMap = useCallback((x: number, y: number) => {
        const container = contentRef.current

        if (container) {
            const mapRect = container.getBoundingClientRect();
            const ratioX = (x - mapRect.x) / mapRect.width
            const ratioY = (y - mapRect.y) / mapRect.height;

            setCenter(ratioX * bounds.width, ratioY * bounds.height, {zoom});
        }
    }, [bounds.height, bounds.width, setCenter, zoom]);
    const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
        setPanning(true);
        setViewportFromMiniMap(event.clientX, event.clientY);
    };
    const handleMouseMove: MouseEventHandler<HTMLDivElement> = (event) => {
        if (panning) {
            setViewportFromMiniMap(event.clientX, event.clientY);
        }
    };
    const handleStopPanning = () => setPanning(false);

    useEffect(() => {
        let minX = 0;
        let maxX = 0;
        let minY = 0;
        let maxY = 0;

        nodes.forEach(node => {
            minX = Math.min(minX, node.position.x);
            maxX = Math.max(maxX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxY = Math.max(maxY, node.position.y);
        });
        setBounds({width: maxX - minX, height: maxY - minY});
    }, [nodes])
    useEffect(() => {
        if (graphData.showMiniMap === undefined) {
            updateModeData({
                showMiniMap: localStorage.getItem(`miniMapDrag.showMiniMap`) !== "false"
            });
        }
    }, [graphData, updateModeData]);
    if (! graphData.showMiniMap) return null;
    return (
        <div className={classes.content} ref={contentRef} onMouseDown={handleMouseDown} onMouseLeave={handleStopPanning}
             onMouseMove={handleMouseMove} onMouseUp={handleStopPanning}>
            <IconButton className={classes.closeButton} onClick={handleCloseMap}
                        onMouseDown={event => {
                            event.stopPropagation();
                        }}>
                <CancelOutlined/>
            </IconButton>
            <MiniMap className={classes.miniMap} nodeColor="black"/>
        </div>
    );
}
const mapStateToProps = (store: never) => ({
    graphData: selectModeData(store) as IGraphData,
});
export default connect(mapStateToProps, {
    updateModeData,
})(MiniMapDrag);
